import { LibraryItem } from '../types';
import { universeCardUrl } from './cardReadiness';

export const CARD_SHEET = { width: 612, height: 792, cardWidth: 162, cardHeight: 243, gap: 0, columns: 3, rows: 3 };
export type CardPrintMode = 'fronts' | 'backs' | 'both';
export function cardPlacement(index: number, back = false) {
  const { width, height, cardWidth, cardHeight, gap, columns, rows } = CARD_SHEET;
  const slot = index % (columns * rows);
  return { page: Math.floor(index / (columns * rows)), x: (width - columns * cardWidth - (columns - 1) * gap) / 2 + (back ? columns - 1 - slot % columns : slot % columns) * (cardWidth + gap), y: (height - rows * cardHeight - (rows - 1) * gap) / 2 + Math.floor(slot / columns) * (cardHeight + gap) };
}

/** Fails the export on unavailable artwork instead of silently producing an incomplete release. */
export async function createCardPdf(cards: LibraryItem[], nodes: HTMLElement[], progress: (message: string) => void, options: { mode?: CardPrintMode; backNodes?: HTMLElement[]; backLinkUrl?: string } = {}): Promise<Blob> {
  if (!cards.length || cards.length !== nodes.length || cards.some(card => card.status !== 'published')) throw new Error('Only published cards can be printed.');
  const mode = options.mode || 'fronts';
  if (mode !== 'fronts' && options.backNodes?.length !== cards.length) throw new Error('Every card needs a matching back.');
  const [{ jsPDF }, { toPng }] = await Promise.all([import('jspdf'), import('html-to-image')]);
  await document.fonts.ready;
  const pdf = new jsPDF({ unit: 'pt', format: 'letter', compress: true });
  pdf.setProperties({ title: 'Overture Universe - published cards' });
  const pageCount = Math.ceil(cards.length / 9) * (mode === 'both' ? 2 : 1);
  for (let page = 1; page < pageCount; page++) pdf.addPage();
  for (const back of mode === 'both' ? [false, true] : [mode === 'backs']) {
    for (let index = 0; index < cards.length; index++) {
      progress(`Preparing ${back ? 'back' : 'front'} ${index + 1} of ${cards.length}…`);
      const node = back ? options.backNodes![index] : nodes[index];
      // Convert images to data URLs up front so capture cannot omit cross-origin or broken artwork.
      for (const img of Array.from(node.querySelectorAll('img'))) {
        img.loading = 'eager';
        if (!img.src.startsWith('data:')) {
          const response = await fetch(img.src);
          if (!response.ok) throw new Error(`Could not load artwork for ${cards[index].title}. Try again before printing.`);
          const blob = await response.blob();
          img.src = await new Promise<string>((resolve, reject) => { const reader = new FileReader(); reader.onload = () => resolve(String(reader.result)); reader.onerror = reject; reader.readAsDataURL(blob); });
        }
        await img.decode().catch(() => { throw new Error(`Could not render artwork for ${cards[index].title}.`); });
      }
      const content = node.querySelector<HTMLElement>('.universe-back-content');
      if (back && content && content.scrollHeight > content.clientHeight + 1) {
        throw new Error(`The back of ${cards[index].title} has too much text to print legibly. Shorten its details before printing.`);
      }
      const png = await toPng(node, { pixelRatio: 3, width: 320, height: 480, backgroundColor: '#ffffff', skipAutoScale: true });
      const { page, x, y } = cardPlacement(index, back);
      pdf.setPage(mode === 'both' ? page * 2 + (back ? 2 : 1) : page + 1);
      pdf.setFontSize(7); pdf.setTextColor(100);
      if (index % 9 === 0) {
        pdf.text(`Sheet ${page + 1} - ${back ? 'BACK - long-edge flip' : 'FRONT'} - Print at 100% / actual size`, CARD_SHEET.width / 2, 12, { align: 'center' });
        // Registration ticks stay in the page margin, leaving adjoining card edges clean.
        const left = (CARD_SHEET.width - CARD_SHEET.columns * CARD_SHEET.cardWidth) / 2;
        const top = (CARD_SHEET.height - CARD_SHEET.rows * CARD_SHEET.cardHeight) / 2;
        pdf.setDrawColor(130); pdf.setLineWidth(0.4);
        for (let column = 0; column <= CARD_SHEET.columns; column++) {
          const markX = left + column * CARD_SHEET.cardWidth;
          pdf.line(markX, top - 10, markX, top - 3);
          pdf.line(markX, top + CARD_SHEET.rows * CARD_SHEET.cardHeight + 3, markX, top + CARD_SHEET.rows * CARD_SHEET.cardHeight + 10);
        }
        for (let row = 0; row <= CARD_SHEET.rows; row++) {
          const markY = top + row * CARD_SHEET.cardHeight;
          pdf.line(left - 10, markY, left - 3, markY);
          pdf.line(left + CARD_SHEET.columns * CARD_SHEET.cardWidth + 3, markY, left + CARD_SHEET.columns * CARD_SHEET.cardWidth + 10, markY);
        }
      }
      pdf.addImage(png, 'PNG', x, y, CARD_SHEET.cardWidth, CARD_SHEET.cardHeight);
      pdf.link(x, y, CARD_SHEET.cardWidth, CARD_SHEET.cardHeight, { url: back && options.backLinkUrl ? options.backLinkUrl : universeCardUrl(cards[index].id) });
    }
  }
  return pdf.output('blob');
}
